import Box from '@/Atoms/Box/Box';
import CCButton from '@/Atoms/CCButton/CCButton';
import CCText from '@/Atoms/CCText';
import CCTextInput from '@/Atoms/CCTextInput';
import useUpdateProfileApi from '@/Hooks/ProfileApi/profileUpdateApi';
import CCColors from '@/Utils/CCColors';
import {FontSizes, hs, vs} from '@/Utils/Dimensions';
import {
  LocationConfig,
  deriveLocationConfig,
  isValidPincode,
} from '@/Utils/PostalCode';
import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';

type Props = {
  pincode: string;
  setPincode: (v: string) => void;
  setShowPincodeModal: (v: boolean) => void;
  setModalDismissed: (v: boolean) => void;
  mobileNumber?: string | null;
  currency?: string | null;
  locationConfig?: LocationConfig | null;
};

const PincodeModal = ({
  pincode,
  setPincode,
  setShowPincodeModal,
  setModalDismissed,
  mobileNumber,
  currency,
  locationConfig,
}: Props) => {
  const [error, setError] = useState('');

  // Use backend-provided config; fall back to client-side derivation.
  // Do NOT pass currency to the fallback — the subscription plan currency is
  // always INR regardless of what the user actually paid, so it's unreliable.
  // The backend location_collection is the only trustworthy currency source.
  const config: LocationConfig =
    locationConfig ?? deriveLocationConfig({mobile_number: mobileNumber});

  const {mutate: updateProfile} = useUpdateProfileApi(
    () => {
      setShowPincodeModal(false);
    },
    (err: any) => {
      setError(err?.error || 'Update failed. Please try again.');
      setPincode('');
    },
  );

  const isPincodeInvalid =
    config.input_field === 'pincode' &&
    (pincode || '').trim() !== '' &&
    !isValidPincode(pincode, {mobile_number: mobileNumber, currency}, config.mode);

  const displayError =
    error || (isPincodeInvalid ? 'Please enter a valid PIN/ZIP code' : '');

  const handlePress = () => {
    if (isPincodeInvalid) {
      setError('Please enter a valid PIN/ZIP code');
      return;
    }
    setModalDismissed(true);

    const updatePayload: Record<string, string> = {
      email: '',
      fide_id: '',
      first_name: '',
      last_name: '',
      mobile_number: '',
      username: '',
      lic_name: '',
      cdc_name: '',
      player_type: '',
      alternate_mobile_number: '',
    };

    if (config.input_field === 'city' || config.mode === 'city') {
      updatePayload.city = pincode;
    } else {
      updatePayload.pincode = pincode;
    }

    updateProfile(updatePayload);
    setPincode(pincode);
  };

  return (
    <View style={styles.wrapper}>
      <Box style={styles.subWrapper} flexDirection="column">
        <CCText fontSize={18} fontWeight="bold" textAlign="center" style={styles.margin}>
          {config.title}
        </CCText>
        <CCText style={{maxWidth: 420}} fontSize={14} textAlign="center">
          {config.description}
        </CCText>
        <CCTextInput
          label={config.placeholder}
          value={pincode}
          onChangeText={text => {
            setPincode(text);
            setError('');
          }}
          style={styles.it}
        />
        <CCText color={CCColors.RedBg} style={styles.er} fontSize={FontSizes[12]}>
          {displayError}
        </CCText>
        <Box style={styles.btn}>
          <CCButton
            contentContainerStyle={styles.update}
            disabled={(pincode || '').trim() === '' || isPincodeInvalid}
            onPress={handlePress}>
            <CCText fontSize={FontSizes[14]} fontWeight={500}>
              Update
            </CCText>
          </CCButton>
        </Box>
      </Box>
    </View>
  );
};

export default PincodeModal;

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    top: '0%',
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10000,
  },
  subWrapper: {backgroundColor: CCColors.White, padding: 20, borderRadius: 8},
  margin: {marginBottom: vs(12)},
  it: {
    width: '100%',
    marginHorizontal: vs(8),
    marginVertical: vs(12),
    minWidth: vs(140),
    padding: vs(12),
  },
  btn: {alignSelf: 'center', marginTop: vs(8)},
  update: {paddingHorizontal: 34, paddingVertical: 6},
  er: {marginLeft: hs(10)},
});
